import React, {useCallback, useState} from "react";
import _ from "lodash";
import {useTranslation} from "react-i18next";
import {Button, ListItemIcon, Menu, MenuItem, Stack, Typography} from "@mui/material";
import AddIcon from "@mui/icons-material/AddOutlined";
import ArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {FarmCreateDialog} from "../dialogs/FarmCreateDialog";
import {FarmsTable} from "../farms/FarmsTable";
import {FieldCreateDialog} from "../dialogs/FieldCreateDialog";
import {useActiveCompanyFarms} from "../../hooks/farmsHooks";
import {useAccessMarker} from "../../hooks/accessMarkerHooks";
import {ViewAuthorizationGuard} from "../ViewAuthorizationGuard";
import {MainViewLayout} from "../MainViewLayout";
import {CompanyHeader} from "./CompanyHeader";
import {ALL_ACCESS_FUNCTIONS} from "../../utils/accessControlUtils";
import {useActiveCompanyOrders} from "../../hooks/ordersHooks";
import {useActiveCompanyFields} from "../../hooks/fieldsHooks";
import {useCompany} from "../../hooks/companiesHooks";

export const CompanyFarmsView: React.FC = () => {
    const {t} = useTranslation();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const [farmCreateDialogOpened, setFarmCreateDialogOpened] = useState<boolean>(false);
    const [fieldCreateDialogOpened, setFieldCreateDialogOpened] = useState<boolean>(false);

    const companyState = useCompany();
    const companyId = companyState.isSuccess && companyState.data !== undefined ? companyState.data.id : null;
    const {items: farms, refresh: farmsRefresh} = useActiveCompanyFarms();

    const access = useAccessMarker(ACCESS_FUNCTIONS);
    const {items: fields, refresh: fieldsRefresh} = useActiveCompanyFields();

    const {refresh: ordersRefresh} = useActiveCompanyOrders();
    const refreshAll = useCallback(() => {
        farmsRefresh();
        fieldsRefresh();
    }, [farmsRefresh, fieldsRefresh]);
    const onFarmCreateDialogClose = (isSuccessful: boolean) => {
        setFarmCreateDialogOpened(false);
        if (isSuccessful) {
            refreshAll();
        }
        setAnchorEl(null);
    };

    const onFieldCreateDialogClose = (isSuccessful: boolean) => {
        setFieldCreateDialogOpened(false);
        if (isSuccessful) {
            refreshAll();
            ordersRefresh();
        }
        setAnchorEl(null);
    };

    return (
        <ViewAuthorizationGuard isAuthorized={access.canViewCompanyFarms}>
            <MainViewLayout header={<CompanyHeader basePath="/organization" />}>
                <Stack direction="column">
                    <Stack direction="row" height="4rem" alignItems="center" justifyContent="space-between">
                        <Typography fontSize={20} fontWeight={500} textTransform="uppercase">
                            {t("companies.farms_view.farms")}
                        </Typography>
                        <div>
                            <Button startIcon={<AddIcon />} onClick={handleClick} endIcon={<ArrowDownIcon />}>
                                {t("companies.farms_view.create")}
                            </Button>
                            <Menu
                                id="basic-menu"
                                anchorEl={anchorEl}
                                open={open}
                                onClose={handleClose}
                                MenuListProps={{
                                    "aria-labelledby": "basic-button",
                                }}
                            >
                                <MenuItem onClick={() => setFarmCreateDialogOpened(true)}>
                                    <ListItemIcon>
                                        <AddIcon />
                                    </ListItemIcon>
                                    {t("companies.farms_view.new_farm")}
                                </MenuItem>
                                <MenuItem onClick={() => setFieldCreateDialogOpened(true)}>
                                    <ListItemIcon>
                                        <AddIcon />
                                    </ListItemIcon>
                                    {t("companies.farms_view.new_field")}
                                </MenuItem>
                            </Menu>
                        </div>
                    </Stack>

                    {companyId && (
                        <FarmsTable
                            farms={farms}
                            refreshFarms={refreshAll}
                            companyId={companyId}
                            refreshFields={refreshAll}
                            fields={fields}
                        />
                    )}
                </Stack>

                {farmCreateDialogOpened && companyId && (
                    <FarmCreateDialog
                        mode="new"
                        companyId={companyId}
                        open={farmCreateDialogOpened}
                        onClose={onFarmCreateDialogClose}
                    />
                )}

                {fieldCreateDialogOpened && companyId && (
                    <FieldCreateDialog
                        companyId={companyId}
                        open={fieldCreateDialogOpened}
                        mode="new"
                        onClose={onFieldCreateDialogClose}
                    />
                )}
            </MainViewLayout>
        </ViewAuthorizationGuard>
    );
};

const ACCESS_FUNCTIONS = _.pick(ALL_ACCESS_FUNCTIONS, ["canViewCompanyFarms"]);
